<template>
  <div>
    <label v-if="withLabel" class="h6 text-muted">{{
      $t("common.company_search.label")
    }}</label>
    <p v-if="withDescription" class="text-muted small">
      {{ $t("common.company_search.description") }}
    </p>

    <SearchableSelectComponent
      :options="companyOptions"
      :modelValue="companyId"
      :placeholder="placeholder || $t('common.company_search.placeholder')"
      :isLoading="isLoading"
      :isError="hasError"
      @update:modelValue="handleSelectCompany"
      @queryUpdated="handleQueryUpdated"
    />
  </div>
</template>

<script setup lang="ts">
import {
  fetchAdminCompanies,
  type AdminCompany,
} from "@/components/common/adminCompaniesApi";
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { computed, onMounted, ref } from "vue";

const props = withDefaults(
  defineProps<{
    placeholder?: string;
    withLabel?: boolean;
    withDescription?: boolean;
    autoFetch?: boolean;
    /** Leave soft-deleted companies out, for pickers that must not land on one. */
    excludeDeleted?: boolean;
  }>(),
  {
    withLabel: true,
    withDescription: true,
    autoFetch: true,
    excludeDeleted: false,
  }
);

const emit = defineEmits<{
  select: [company: AdminCompany];
  queryUpdated: [query: string];
}>();

const companyId = defineModel<number | null>({ default: null });

interface CompanyOption {
  label: string;
  value: number;
  description?: string;
  _company: AdminCompany;
}

const isLoading = ref(false);
const hasError = ref(false);
const companies = ref<AdminCompany[]>([]);
const { globalErrorHandler } = useGlobalHandlers();

const companyOptions = computed<CompanyOption[]>(() =>
  companies.value
    .filter((company) => !(props.excludeDeleted && company.deleted_at))
    .map((company) => ({
      label: company.deleted_at ? `${company.name} (deleted)` : company.name,
      value: company.id,
      description: company.country
        ? `${company.country} - ID: ${company.id}`
        : `ID: ${company.id}`,
      _company: company,
    }))
);

const fetchCompanies = async () => {
  try {
    isLoading.value = true;
    hasError.value = false;
    companies.value = await fetchAdminCompanies();
  } catch (error) {
    globalErrorHandler(error);
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

// SearchableSelectComponent's model is untyped; clearing it reports `undefined`.
const handleSelectCompany = (value: unknown) => {
  const selectedCompany = companies.value.find((c) => c.id === value);
  if (selectedCompany) {
    emit("select", selectedCompany);
  }
  companyId.value = selectedCompany?.id ?? null;
};

const handleQueryUpdated = (query: string) => {
  emit("queryUpdated", query);
};

onMounted(() => {
  if (props.autoFetch) {
    fetchCompanies();
  }
});

defineExpose({
  fetchCompanies,
});
</script>

<style scoped lang="scss"></style>
